import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db7svbf3hiaolq192lg0";

type Status = "idle" | "submitting" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = ((formData.get("contact_alt") as string) || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      payload.append(key, value.toString());
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      if (response.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试。");
    }
  };

  const inputClass =
    "w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors";
  const labelClass = "block text-sm font-medium text-foreground-700 mb-1.5";

  return (
    <form
      data-readdy-form
      id="db7svbf3hiaolq192lg0"
      onSubmit={handleSubmit}
      className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-7"
    >
      <h2 className="font-heading font-extrabold text-lg md:text-xl text-foreground-950 mb-1">
        留言反馈
      </h2>
      <p className="text-sm text-foreground-500 mb-5">
        对网站有任何建议、内容纠错或合作意向，欢迎给我们留言。
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="fb-name" className={labelClass}>
            称呼
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            placeholder="您的称呼"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="fb-email" className={labelClass}>
            邮箱
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className={inputClass}
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-subject" className={labelClass}>
          反馈类型
        </label>
        <select id="fb-subject" name="subject" className={inputClass} defaultValue="">
          <option value="" disabled>
            请选择反馈类型
          </option>
          <option value="内容建议">内容建议</option>
          <option value="内容纠错">内容纠错</option>
          <option value="使用问题">使用问题</option>
          <option value="商务合作">商务合作</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-message" className={labelClass}>
          留言内容
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          placeholder="请输入您的留言（最多 500 字）"
          className={`${inputClass} resize-none`}
        />
      </div>

      {/* anti-spam honeypot */}
      <div className="form-hp-wrap" aria-hidden="true">
        <label htmlFor="fb-contact-alt">Alternate contact</label>
        <input
          id="fb-contact-alt"
          name="contact_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === "submitting" ? (
            <>
              <i className="ri-loader-4-line animate-spin" /> 提交中…
            </>
          ) : (
            <>
              <i className="ri-send-plane-line" /> 提交留言
            </>
          )}
        </button>

        {status === "success" && (
          <span className="text-sm text-primary-600 flex items-center gap-1.5">
            <i className="ri-checkbox-circle-fill" /> 感谢您的留言，我们会尽快处理！
          </span>
        )}
        {status === "error" && (
          <span className="text-sm text-accent-700 flex items-center gap-1.5">
            <i className="ri-error-warning-fill" /> {formError}
          </span>
        )}
      </div>
    </form>
  );
}